import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Award, Users, BookOpen, Calendar, Star, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { FooterSection } from "@/components/FooterSection";
import { SEOHead } from "@/components/SEOHead";
import { breadcrumbSchema } from "@/data/schemas";
import { InquiryForm } from "@/components/InquiryForm";

const timeline = [
  { year: "1976", title: "Founded", desc: "Emcee Sons established on Avenue Road, Bengaluru as a diary and stationery house." },
  { year: "2000s", title: "Customisation Unit", desc: "Set up our in-house customisation unit for gold foiling, page insertions, and bespoke diaries." },
  { year: "Today", title: "Trusted by 1000+", desc: "Serving businesses across India with premium diaries, planners, journals, and corporate gifting solutions." },
];

const values = [
  { icon: Award, title: "Quality First", desc: "Every diary reflects our commitment to premium materials and craftsmanship." },
  { icon: Users, title: "Customer Focused", desc: "We treat every order as personal — from 10 pieces to 10,000." },
  { icon: BookOpen, title: "50+ Variants", desc: "The widest range of diaries, planners, and notebooks under one roof." },
  { icon: Calendar, title: "Since 1976", desc: "Nearly five decades of trust, experience, and reliability." },
];

export default function AboutPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="about-page">
      <SEOHead
        title="About Emcee Sons - Super Stockist for Eagle Diaries Since 1976"
        description="Emcee Sons is Bengaluru's trusted Super Stockist for Eagle Diaries since 1976. Located on Avenue Road with a showroom and in-house customisation unit for gold foiling, page insertions and bespoke diaries."
        path="/about"
        image="/about_us.png"
        schema={breadcrumbSchema([{ name: "Home", url: "/" }, { name: "About Us" }])}
      />
      <Navbar />

      {/* Hero */}
      <div className="pt-20 relative overflow-hidden">
        <div className="bg-[#7A1A31] py-20 sm:py-28">
          <div className="container mx-auto px-6 sm:px-8 max-w-7xl text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="text-xs tracking-[0.25em] uppercase text-[#C8A23A] font-semibold mb-4 block" style={{ fontFamily: "'Outfit', sans-serif" }}>Our Story</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }} data-testid="about-page-heading">
                About Emcee Sons
              </h1>
              <p className="text-base text-white/60 max-w-xl mx-auto" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>
                Super Stockist for Eagle Diaries since 1976. Showroom & customisation unit on Avenue Road, Bengaluru.
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Image + Story */}
      <div className="container mx-auto px-6 sm:px-8 max-w-7xl py-20 sm:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <div className="rounded-2xl overflow-hidden shadow-2xl shadow-black/10">
              <img src="/about_us.png" alt="Emcee Sons Showroom" className="w-full h-[400px] object-cover" />
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.15 }}>
            <h2 className="text-2xl sm:text-3xl text-[#1A1A1A] tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Bengaluru's Premier <span className="text-[#7A1A31]">Diary House</span>
            </h2>
            <p className="text-base text-[#6B7280] leading-relaxed mb-5" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>
              Emcee Sons is the Super Stockist for Eagle Diaries, one of India's most trusted diary brands. Since 1976, our showroom and customisation unit on Avenue Road has been the go-to destination for businesses seeking premium New Year diaries, planners, journals, and corporate gifts.
            </p>
            <p className="text-base text-[#6B7280] leading-relaxed mb-5" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>
              We offer gold foiling with company logos, personalised name embossing, page insertions, and fully bespoke diary manufacturing for bulk orders of 2000+ units. Our 50+ diary variants cater to every corporate need — from compact A5 planners to executive B5 diaries.
            </p>
            <div className="flex items-center gap-2 text-[#C8A23A]">
              <Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" />
              <span className="text-sm text-[#6B7280] ml-2" style={{ fontFamily: "'Outfit', sans-serif" }}>4.6 on Google</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Values */}
      <div className="bg-white py-20 sm:py-28">
        <div className="container mx-auto px-6 sm:px-8 max-w-7xl">
          <h2 className="text-2xl sm:text-3xl text-center text-[#1A1A1A] tracking-tight mb-16" style={{ fontFamily: "'Playfair Display', serif" }}>What Sets Us Apart</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v, i) => (
              <motion.div key={v.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center p-6">
                <div className="w-14 h-14 rounded-full bg-[#7A1A31]/10 flex items-center justify-center mx-auto mb-5">
                  <v.icon className="w-6 h-6 text-[#7A1A31]" />
                </div>
                <h3 className="text-lg text-[#1A1A1A] mb-2" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>{v.title}</h3>
                <p className="text-sm text-[#6B7280]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="container mx-auto px-6 sm:px-8 max-w-4xl py-20 sm:py-28">
        <h2 className="text-2xl sm:text-3xl text-center text-[#1A1A1A] tracking-tight mb-16" style={{ fontFamily: "'Playfair Display', serif" }}>Our Journey</h2>
        <div className="space-y-12">
          {timeline.map((t, i) => (
            <motion.div key={t.year} initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="flex gap-8 items-start">
              <div className="flex-shrink-0 w-20 text-right">
                <span className="text-lg font-bold text-[#7A1A31]" style={{ fontFamily: "'Playfair Display', serif" }}>{t.year}</span>
              </div>
              <div className="flex-shrink-0 w-3 h-3 rounded-full bg-[#C8A23A] mt-2 ring-4 ring-[#C8A23A]/20" />
              <div>
                <h3 className="text-base font-semibold text-[#1A1A1A] mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{t.title}</h3>
                <p className="text-sm text-[#6B7280]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>{t.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* CTA + Inquiry Form — unique split layout */}
      <div className="relative overflow-hidden" data-testid="about-cta-section">
        {/* Background */}
        <div className="absolute inset-0 bg-[#1A1A1A]" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#7A1A31]/30 to-transparent" aria-hidden="true" />
        <div className="absolute bottom-0 left-1/4 w-80 h-80 rounded-full bg-[#C8A23A]/5 blur-3xl" aria-hidden="true" />

        <div className="relative container mx-auto px-6 sm:px-8 max-w-7xl py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            {/* Left — bold CTA text */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <span
                className="text-xs tracking-[0.25em] uppercase text-[#C8A23A] font-semibold mb-5 block"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Start Your Order
              </span>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight mb-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Let's Create<br />
                Something <span className="text-[#C8A23A]">Exceptional</span>
              </h2>
              <p
                className="text-base text-white/50 leading-relaxed mb-10 max-w-md"
                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
              >
                Whether you need 100 branded diaries or 10,000 fully customised ones — share your requirements and we'll make it happen.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6">
                {[
                  { val: "49+", label: "Years" },
                  { val: "1000+", label: "Clients" },
                  { val: "50+", label: "Variants" },
                ].map((s) => (
                  <div key={s.label}>
                    <span className="text-2xl sm:text-3xl font-bold text-white block" style={{ fontFamily: "'Playfair Display', serif" }}>{s.val}</span>
                    <span className="text-xs text-white/40 tracking-wider uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.label}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link to="/shop" className="inline-flex items-center justify-center gap-2 bg-white/[0.08] border border-white/10 text-white px-7 py-3.5 rounded-full hover:bg-white/15 transition-all text-sm font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Browse Products <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-white/[0.08] border border-white/10 text-white px-7 py-3.5 rounded-full hover:bg-white/15 transition-all text-sm font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Contact Us
                </Link>
              </div>
            </motion.div>

            {/* Right — form on dark bg */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-2xl p-8 sm:p-10"
            >
              <h3
                className="text-xl text-white mb-1"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Get a Free Quote
              </h3>
              <p
                className="text-sm text-white/40 mb-8"
                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
              >
                Our team responds within 24 hours
              </p>
              <InquiryForm source="about" variant="dark" />
            </motion.div>
          </div>
        </div>
      </div>

      <FooterSection />
    </div>
  );
}
